import { inject, Injectable } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Subject } from 'rxjs';
import {
  IBulkActionError,
  IBulkActionHandleOptions,
  BulkActionNotificationType,
  IBulkActionHandleResult,
  IBulkActionResult,
} from '../interfaces/IBulkActionResult';
import { ICrudBulkOperationError } from '../interfaces/ICrudError';
import { localizeWithFallback } from '../utils/localization-utils';

@Injectable({
  providedIn: 'root',
})
export class ZnBulkActionResultService {
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  private readonly errorSubject = new Subject<ICrudBulkOperationError>();
  readonly errorData$ = this.errorSubject.asObservable();
  private readonly completedSubject = new Subject<IBulkActionHandleResult>();
  readonly completed$ = this.completedSubject.asObservable();

  handle(
    result: Partial<IBulkActionResult> | Record<string, any> | null | undefined,
    options: IBulkActionHandleOptions = {},
  ): IBulkActionHandleResult {
    const normalizedResult = this.normalize(result, options);

    this.showToast(normalizedResult);
    this.emitRowErrors(normalizedResult);

    const handleResult = {
      hasProcessedRecords: normalizedResult.successCount > 0,
    };

    this.completedSubject.next(handleResult);

    return handleResult;
  }

  private normalize(
    result: Partial<IBulkActionResult> | Record<string, any> | null | undefined,
    options: IBulkActionHandleOptions,
  ): IBulkActionResult {
    const response = (result ?? {}) as Record<string, any>;
    const errors = this.normalizeErrors(response);
    const errorCount = response.errorCount ?? response.failedCount ?? errors.length;
    const successCount =
      response.successCount ??
      response.successfulCount ??
      this.resolveSuccessCount(options.selectedCount, errorCount, result);

    return {
      action: response.action,
      title: response.title,
      successCount,
      errorCount,
      message: response.message?.trim() || this.getFallbackMessage(),
      notificationType:
        this.normalizeNotificationType(response.notificationType) ??
        this.resolveNotificationType({ successCount, errorCount }),
      errors,
    };
  }

  private resolveSuccessCount(
    selectedCount: number | undefined,
    errorCount: number,
    rawResult: unknown,
  ): number {
    if (selectedCount === undefined) {
      return 0;
    }

    if (rawResult === null || rawResult === undefined) {
      return selectedCount;
    }

    return Math.max(selectedCount - errorCount, 0);
  }

  private normalizeErrors(result: Record<string, any>): IBulkActionError[] {
    const rawErrors = this.getRawErrors(result);

    return rawErrors
      .map((error): IBulkActionError | undefined => {
        const id = this.getFirstDefinedNumber(error, [
          'id',
          'interventionId',
          'standId',
          'standCycleRotationId',
        ]);
        const message = error.message ?? error.errorMessage ?? '';

        if (id === undefined) {
          return undefined;
        }

        return {
          id,
          label: error.label ?? error.standName,
          message,
        };
      })
      .filter((error): error is IBulkActionError => !!error);
  }

  private getRawErrors(result: Record<string, any>): any[] {
    if (Array.isArray(result.errors)) {
      return result.errors;
    }

    if (Array.isArray(result.failedItems)) {
      return result.failedItems;
    }

    return [];
  }

  private getFirstDefinedNumber(
    source: Record<string, any>,
    keys: string[],
  ): number | undefined {
    for (const key of keys) {
      const value = source?.[key];
      if (typeof value === 'number') {
        return value;
      }
    }

    return undefined;
  }

  private normalizeNotificationType(
    notificationType: unknown,
  ): BulkActionNotificationType | undefined {
    return notificationType === 'success' ||
      notificationType === 'warning' ||
      notificationType === 'error'
      ? notificationType
      : undefined;
  }

  private resolveNotificationType(result: {
    successCount?: number;
    errorCount?: number;
  }): BulkActionNotificationType {
    const successCount = result.successCount ?? 0;
    const errorCount = result.errorCount ?? 0;

    if (errorCount === 0) {
      return 'success';
    }

    return successCount > 0 ? 'warning' : 'error';
  }

  private showToast(result: IBulkActionResult): void {
    switch (result.notificationType) {
      case 'success':
        this.toaster.success(result.message);
        break;
      case 'warning':
        this.toaster.warn(result.message);
        break;
      case 'error':
        this.toaster.error(result.message);
        break;
    }
  }

  private emitRowErrors(result: IBulkActionResult): void {
    this.errorSubject.next({
      title: result.title,
      failedRecords: result.errors.map(error => ({
        id: error.id,
        message: error.message,
      })),
    });
  }

  private getFallbackMessage(): string {
    return localizeWithFallback(
      this.localizationService,
      'LanguageService::BulkActionFallbackMessage'
    );
  }
}
